<template>
  <v-dialog
    v-model="internalValue"
    max-width="800"
    transition="dialog-top-transition"
  >
  <v-card class="premium-modal-card">
      <div class="premium-modal-header">
        <img :src="logo" alt="BRPoints" class="premium-modal-logo" />
        <h3 class="premium-modal-title">{{ title }}</h3>
        <p class="premium-modal-subtitle">{{ subtitle }}</p>
      </div>
      <v-card-text class="premium-modal-body">
        <v-row v-if="hasQrcode" class="premium-payment-guide-row my-2">
          <v-col cols="12">
            <p class="premium-payment-guide-description">
              Basta abrir “<b>Pague e Ganhe</b>” no app <b>BRPoints</b>, escanear o QR acima ou copiar o código e colar na mesma
              tela. Com isso você garante a ativação em segundos, permanece dentro da experiência <b>BRPoints</b> e
              ainda acumula <b>10% de cashback direto no app</b>.
            </p>
          </v-col>
        </v-row>
        <div v-if="!hasQrcode">
          <div
            v-for="feature in features"
            :key="feature"
            class="premium-feature-item mb-4"
          >
            <v-icon color="#2dcc6b" size="20">mdi-check-bold</v-icon>
            <span>{{ feature }}</span>
          </div>
          <v-text-field
            v-model="internalDocument"
            label="CPF do responsável pelo pagamento"
            placeholder="000.000.000-00"
            :rules="documentRules"
            outlined
            dense
            clearable
            prepend-icon="mdi-credit-card"
            v-mask="'###.###.###-##'"
          />
          <div class="premium-investment-highlight mb-2">
            <span class="investment-label">INVESTIMENTO ÚNICO</span>
            <strong class="investment-value">R$ 179,99</strong>
            <span class="investment-footnote">Pagamento 100% seguro</span>
          </div>
          <div class="premium-cta-row">
            <v-btn
              block
              color="#e5183d"
              dark
              class="premium-payment-btn"
              :loading="loading"
              :disabled="loading"
              @click="handleCta"
            >
              <v-icon left>mdi-handedness</v-icon>
              {{ ctaText }}
            </v-btn>
            <small class="premium-cta-description">{{ ctaDescription }}</small>
          </div>
        </div>
        <v-row v-if="hasQrcode" class="premium-qrcode-row" dense>
          <v-col cols="12">
            <v-img
              class="premium-qrcode-image"
              :src="qrcodeImage"
              width="220"
              height="220"
              contain
            />
            <v-btn
              class="premium-copy-btn"
              small
              color="#e5183d"
              dark
              @click="copyQRCode"
            >
              Clique para copiar
            </v-btn>
            <div class="premium-copy-feedback" v-if="showCopySuccess">
              Código copiado!
            </div>
            <v-btn
              class="premium-already-paid-btn mt-4"
              block
              outlined
              color="#2dcc6b"
              :loading="checkingPremium"
              :disabled="checkingPremium"
              @click="handleAlreadyPaid"
            >
              Já paguei e agora sou Premium
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="premium-modal-actions">
        <v-btn text color="grey darken-1" @click="close">
          Decidir depois
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import brandLogo from "@/assets/logo.webp";

export default defineComponent({
  name: "PremiumModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Seja um Parceiro Oficial",
    },
    subtitle: {
      type: String,
      default: "Desbloqueie todo o potencial do seu negócio!",
    },
    features: {
      type: Array as () => string[],
      default: () => [
        "Fidelização de Clientes Automatizada",
        "Cashback Instantâneo para seu Negócio",
        "Bônus de Indicação Recorrente",
        "Relatórios de Desempenho",
      ],
    },
    ctaText: {
      type: String,
      default: "Liberar Premium e crescer com BRPoints",
    },
    ctaDescription: {
      type: String,
      default: "Você recebe liberação imediata após o pagamento seguro.",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    customerDocument: {
      type: String,
      default: "",
    },
    qrcodeImage: {
      type: String,
      default: "",
    },
    qrcodeText: {
      type: String,
      default: "",
    },
    transactionId: {
      type: String,
      default: "",
    },
    checkingPremium: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["input", "pay", "update:customerDocument", "checkPremium"],
  setup(props, { emit }) {
    const internalValue = computed({
      get: () => props.value,
      set: (value: boolean) => emit("input", value),
    });

    const close = () => {
      emit("input", false);
    };
    const handleCta = () => {
      emit("pay");
    };
    const internalDocument = computed({
      get: () => props.customerDocument,
      set: (value: string) => emit("update:customerDocument", value),
    });
    const documentRules = [
      (v: string) => {
        if (!v) return "CPF é obrigatório";
        const numeric = v.replace(/\D/g, "");
        return numeric.length === 11 || "Informe 11 dígitos";
      },
    ];
    const hasQrcode = computed(() => Boolean(props.qrcodeImage || props.qrcodeText));
    const qrTextToCopy = computed(() => props.qrcodeText || "");
    const showCopySuccess = ref(false);
    let copyTimer: number | null = null;

    const copyQRCode = async () => {
      if (!qrTextToCopy.value) return;
      try {
        await navigator.clipboard.writeText(qrTextToCopy.value);
        showCopySuccess.value = true;
        if (copyTimer) {
          window.clearTimeout(copyTimer);
        }
        copyTimer = window.setTimeout(() => {
          showCopySuccess.value = false;
          copyTimer = null;
        }, 2000);
      } catch (error) {
        console.error("Erro ao copiar o QR code:", error);
      }
    };

    const handleAlreadyPaid = () => {
      emit("checkPremium");
    };

    return {
      internalValue,
      close,
      logo: brandLogo,
      handleCta,
      internalDocument,
      documentRules,
      hasQrcode,
      copyQRCode,
      showCopySuccess,
      handleAlreadyPaid,
    };
  },
});
</script>

<style scoped lang="scss">
.premium-modal-card {
  border-radius: 16px;
  overflow: hidden;
}

.premium-modal-header {
  background: #e5183d;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #fff;
  text-align: center;
  align-items: center;
  margin-bottom: 16px;
}

.premium-modal-logo {
  height: 48px;
}

.premium-modal-title {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
}

.premium-modal-subtitle {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
}

.premium-modal-body {
  padding: 24px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #ffffff;
}

.premium-feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  color: #222;
}

.premium-investment-highlight {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.investment-label {
  letter-spacing: 1px;
  display: block;
  color: #777;
  font-size: 0.9rem;
}

.investment-value {
  display: block;
  font-size: 2rem;
  margin: 6px 0;
  color: #111;
}

.investment-footnote {
  color: #4f4f4f;
  font-size: 0.95rem;
}

.premium-payment-btn {
  height: 92px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 14px;
  padding: 0 16px;
  text-transform: none;
}

.premium-cta-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.premium-cta-description {
  font-size: 0.8rem;
  color: #555;
  text-align: center;
  margin: 0;
}

.premium-payment-guide-row {
  background: rgba(229, 24, 61, 0.08);
  border-radius: 12px;
  padding: 12px;
}

.premium-payment-guide-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #c41e3a;
  margin-bottom: 8px;
}

.premium-payment-guide-description {
  font-size: 0.9rem;
  color: #333;
  line-height: 1.4;
  margin: 0;
}

.premium-qrcode-row {
  margin-top: 12px;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.premium-qrcode-image {
  display: block;
  margin: 0 auto;
}

.premium-copy-btn {
  margin: 12px auto 0;
  display: block;
}

.premium-copy-feedback {
  font-size: 0.75rem;
  color: #4a4a4a;
  margin-top: 6px;
}

.premium-qrcode-text {
  font-size: 0.85rem;
  margin-top: 12px;
  word-break: break-all;
}

.premium-transaction-id {
  font-size: 0.75rem;
  color: #757575;
  margin-top: 4px;
}

.premium-cta-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.premium-cta-description {
  font-size: 0.8rem;
  color: #555;
  text-align: center;
  margin: 0;
}

.premium-already-paid-btn {
  text-transform: none;
  font-weight: 600;
}

.premium-modal-actions {
  justify-content: center;
  padding-bottom: 16px;
}
</style>

